import { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import api, { apiError } from "@/lib/api";
import { formatRupiah } from "@/lib/format";
import { toast } from "sonner";
import { UploadCloud, FileCheck2, X, Check, Loader2 } from "lucide-react";
import DatePicker from "@/components/DatePicker";

export default function PayProofDialog({ target, onClose, onDone }) {
  const [file, setFile] = useState(null);
  const [date, setDate] = useState("");
  const edit = target?.edit;
  useEffect(() => { setDate(target?.edit?.date || ""); }, [target]);
  const [progress, setProgress] = useState(0);
  const [busy, setBusy] = useState(false);
  const [drag, setDrag] = useState(false);
  const inp = useRef(null);
  const reset = () => { setFile(null); setProgress(0); setBusy(false); };
  const close = () => { if (!busy) { reset(); onClose(); } };
  const amount = target ? (edit ? edit.credit : target.t ? target.t.amount : target.s.net_amount) : 0;

  const pick = (f) => {
    if (!f) return;
    if (f.size > 10 * 1024 * 1024) { toast.error("Ukuran file maksimal 10MB"); return; }
    setFile(f);
  };

  const submit = async () => {
    setBusy(true);
    try {
      let proof_id = null;
      if (file) {
        const fd = new FormData();
        fd.append("file", file);
        const r = await api.post("/proofs", fd, { onUploadProgress: (e) => setProgress(Math.round((e.loaded / (e.total || 1)) * 100)) });
        proof_id = r.data.id;
      }
      if (edit) await api.put(`/schedules/${target.s.id}/payment`, { tranche_id: target.t?.id || null, proof_id, paid_date: date || null });
      else await api.post(`/schedules/${target.s.id}/mark-paid`, { tranche_id: target.t?.id || null, proof_id });
      toast.success(edit ? "Pembayaran diperbarui" : "Pembayaran tercatat di Buku Besar");
      reset(); onDone();
    } catch (e) { toast.error(apiError(e)); setBusy(false); setProgress(0); }
  };

  return (
    <Dialog open={!!target} onOpenChange={(o) => { if (!o) close(); }}>
      <DialogContent className="rounded-[28px] sm:max-w-md" data-testid="pay-proof-dialog">
        <DialogHeader>
          <DialogTitle>{edit ? "Edit Pembayaran" : "Konfirmasi Sudah Ditransfer"}</DialogTitle>
          <DialogDescription>{edit ? `${edit.type} · ${edit.ref}` : `${target?.s.code}${target?.t ? " · termin" : ""}`} · <span className="font-semibold text-slate-900">{formatRupiah(amount)}</span></DialogDescription>
        </DialogHeader>
        {edit && <div><div className="text-xs text-slate-500 mb-1">Tanggal bayar</div><DatePicker value={date} onChange={setDate} testid="edit-pay-date" /></div>}
        {edit?.proof_id && !file && <div className="text-xs text-emerald-700 -mb-1">Sudah ada bukti. Upload baru untuk mengganti.</div>}
        <div
          onClick={() => !busy && inp.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
          onDragLeave={() => setDrag(false)}
          onDrop={(e) => { e.preventDefault(); setDrag(false); pick(e.dataTransfer.files?.[0]); }}
          className={`relative overflow-hidden cursor-pointer rounded-3xl border-2 border-dashed p-6 text-center transition-all duration-300 ${drag ? "border-blue-500 bg-blue-50 scale-[1.02]" : file ? "border-emerald-300 bg-emerald-50/50" : "border-slate-200 hover:border-blue-300 hover:bg-slate-50"}`}
          data-testid="proof-dropzone">
          <input ref={inp} type="file" accept=".jpg,.jpeg,.png,.webp,.heic,.pdf" className="hidden" onChange={(e) => { pick(e.target.files?.[0]); e.target.value = ""; }} data-testid="proof-input" />
          {busy && file && <div className="absolute inset-x-0 bottom-0 h-1.5 bg-slate-100"><div className="h-full bg-gradient-to-r from-blue-500 to-emerald-500 transition-[width] duration-300" style={{ width: `${progress}%` }} /></div>}
          {!file ? (
            <div className="flex flex-col items-center gap-2">
              <div className={`h-14 w-14 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center ${drag ? "animate-bounce" : "animate-[float_2.4s_ease-in-out_infinite]"}`}><UploadCloud className="h-7 w-7" /></div>
              <div className="text-sm font-semibold text-slate-800">Upload bukti transfer <span className="font-normal text-slate-400">(opsional)</span></div>
              <div className="text-xs text-slate-400">Ketuk atau seret file ke sini · JPG, PNG, PDF · maks 10MB</div>
            </div>
          ) : (
            <div className="flex items-center gap-3 text-left animate-in fade-in zoom-in-95 duration-300">
              <div className="h-12 w-12 shrink-0 rounded-2xl bg-emerald-100 text-emerald-700 flex items-center justify-center">
                {busy ? (progress >= 100 ? <Loader2 className="h-6 w-6 animate-spin" /> : <span className="text-xs font-bold">{progress}%</span>) : <FileCheck2 className="h-6 w-6" />}
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold text-slate-800 truncate" data-testid="proof-filename">{file.name}</div>
                <div className="text-xs text-slate-400">{busy ? (progress >= 100 ? "Menyimpan…" : "Mengunggah…") : `${(file.size / 1024).toFixed(0)} KB · siap diunggah`}</div>
              </div>
              {!busy && <button type="button" onClick={(e) => { e.stopPropagation(); setFile(null); }} className="h-8 w-8 rounded-full hover:bg-white flex items-center justify-center text-slate-400" data-testid="proof-remove"><X className="h-4 w-4" /></button>}
            </div>
          )}
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={close} disabled={busy} data-testid="proof-cancel">Batal</Button>
          <Button className="bg-blue-600 hover:bg-blue-700 text-white" onClick={submit} disabled={busy} data-testid="proof-confirm">
            {busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Check className="h-4 w-4 mr-1.5" />}{file ? (edit ? "Upload & Simpan" : "Upload & Tandai Lunas") : (edit ? "Simpan" : "Tandai Lunas")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
